import React, { useState, useEffect } from 'react';
import { 
  CheckCircle2, 
  AlertTriangle, 
  Play, 
  History, 
  ShieldCheck, 
  ShieldAlert, 
  Sparkles,
  ChevronDown,
  ChevronUp,
  Download,
  ArrowRight,
  Info
} from 'lucide-react';
import { api, ValidationHistoryRecord, ValidationRunReport } from '../services/api';
import { Agent } from '../types';

interface ValidationProps {
  setActiveTab: (tab: string) => void;
  readOnly?: boolean;
  demoMode?: boolean;
  role?: 'owner' | 'admin' | 'operator' | 'viewer';
}

export const ReleaseValidation: React.FC<ValidationProps> = ({ setActiveTab, readOnly = false, demoMode = false, role = 'admin' }) => {
  const [selectedAgentId, setSelectedAgentId] = useState<string>('agent-fraud-02');
  const [agentName, setAgentName] = useState<string>('Fraud Analysis Agent');
  const [version, setVersion] = useState<string>('v2.0.0-rc1');
  const [running, setRunning] = useState<boolean>(false);
  const [currentReport, setCurrentReport] = useState<ValidationRunReport | null>(null);
  const [history, setHistory] = useState<ValidationHistoryRecord[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [expandedSuite, setExpandedSuite] = useState<number | null>(0);
  const [candidateAgents, setCandidateAgents] = useState<Agent[]>([]);
  const [loadingAgents, setLoadingAgents] = useState<boolean>(true);
  const [releasePending, setReleasePending] = useState<boolean>(false);
  const [releaseBlocked,setReleaseBlocked]=useState(false);
  const [releaseNote, setReleaseNote] = useState<string>('');
  const [exporting, setExporting] = useState<boolean>(false);
  const [isHistoryCollapsed, setIsHistoryCollapsed] = useState<boolean>(false);

  const loadAgents = async () => {
    setLoadingAgents(true);
    try {
      const data = await api.getAgents();
      setCandidateAgents(data);
      const selected = data.find((agent) => agent.id === selectedAgentId) || data[0];
      if (selected) {
        setSelectedAgentId(selected.id);
        setAgentName(selected.name);
        setVersion(selected.version);
      }
    } catch (err) {
      console.error('Error fetching validation candidates:', err);
      setError(err instanceof Error ? err.message : 'Validation candidates are unavailable.');
    } finally {
      setLoadingAgents(false);
    }
  };

  const loadHistory = async () => {
    try {
      const data = await api.getValidationHistory();
      setHistory(data);
      const recorded=data.find(run=>run.id===currentReport?.run_id);
      if(recorded?.release_decision==='approved'||recorded?.release_decision==='rejected'){
        setCurrentReport(previous=>previous?{...previous,release_decision:recorded.release_decision!}:null);setReleaseBlocked(false);
      }
    } catch (err) {
      console.error('Error fetching validation history:', err);
      setError(err instanceof Error ? err.message : 'Validation history is unavailable.');
    }
  };

  useEffect(() => {
    loadHistory();
    loadAgents();
  }, []);

  const handleRunValidation = async (agentId?: string, name?: string, ver?: string) => {
    const aid = agentId || selectedAgentId;
    const aname = name || agentName;
    const aver = ver || version;

    if (!aid || !aver.trim()) {
      setError('Select an agent and enter a release version before running validation.');
      return;
    }

    setRunning(true);
    setReleaseBlocked(false);
    setCurrentReport(null);
    setError(null);
    try {
      const report = await api.validateAgent(aid, aname, aver);
      setCurrentReport(report);
      setReleaseNote('');
      await loadHistory();
    } catch (err) {
      console.error('Error running validation suite:', err);
      setError(err instanceof Error ? err.message : 'Validation could not be completed.');
    } finally {
      setRunning(false);
    }
  };

  const handleReleaseDecision = async (decision: 'approve' | 'reject') => {
    if (!currentReport?.run_id || releasePending || releaseBlocked || readOnly) return;
    setReleasePending(true);
    setError(null);
    try {
      const result = await api.releaseValidation(currentReport.run_id, decision, releaseNote);
      setCurrentReport(previous => previous ? ({ ...previous, release_decision: result.release_decision, agent_status: result.agent_status }) : null);
      await Promise.all([loadHistory(), loadAgents()]);
    } catch (err) {
      setReleaseBlocked(true);
      // A timeout is unknown. Read the recorded run before offering another decision.
      try {
        const latest=await api.getValidationHistory();setHistory(latest);
        const recorded=latest.find(run=>run.id===currentReport.run_id);
        if(recorded?.release_decision==='approved'||recorded?.release_decision==='rejected'){
          setCurrentReport(previous=>previous?{...previous,release_decision:recorded.release_decision!}:null);setReleaseBlocked(false);
          setError('The response was interrupted. The recorded decision was confirmed from validation history.');
        }else setError((err instanceof Error?err.message:'Release outcome is not confirmed.')+' No decision was retried automatically. Refresh validation history to check its outcome, or run new safety checks.');
      }catch{setError('The release outcome is unknown and history could not be checked. No decision was retried automatically. Refresh validation history to check its outcome, or run new safety checks.');}
    } finally {
      setReleasePending(false);
    }
  };

  const handleExportReport = async () => {
    if (!currentReport) return;
    setExporting(true);
    setError(null);
    try {
      const result = await api.exportComplianceReport(currentReport, 'markdown');
      const blob = new Blob([result.content], { type: 'text/markdown;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = result.filename;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Compliance report could not be exported.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-8 animate-fade-in max-w-6xl mx-auto">
      
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight">
          Release validation
        </h1>
        <p className="text-slate-600 dark:text-slate-400 text-sm mt-1.5 max-w-3xl">
          Run the safety checks for a profile version before promoting it to Protected.
        </p>
      </div>

      {readOnly && (
        <div className="rounded-xl border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-900/60 p-3.5 text-xs text-slate-700 dark:text-slate-300 flex items-center gap-2">
          <Info className="w-4 h-4 text-brand-600 dark:text-brand-400 shrink-0" />
          <span>
            {role === 'viewer'
              ? '👁️ Read-Only Viewer: Historical validation results are available to explore. Running safety checks and recording release promotion decisions are locked.'
              : 'Read-only workspace: your role can inspect validation history but cannot run checks or record release decisions.'}
          </span>
        </div>
      )}

      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 text-xs leading-5 text-slate-600 dark:border-slate-800 dark:bg-slate-950/50 dark:text-slate-400">
        <strong className="text-slate-900 dark:text-white">When is a profile accepted?</strong> All required checks must pass, then an authorized human approves the release. A flagged or failed check stays in Review Required and cannot be promoted automatically. Rejecting a run records the decision and keeps the profile out of Protected status.
      </div>

      {error && (
        <div role="alert" className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-xs text-rose-800 dark:border-rose-900/50 dark:bg-rose-950/20 dark:text-rose-300">
          {error}
        </div>
      )}

      {/* Target Agent Selector & Trigger */}
      <div className="glass-panel p-6 rounded-2xl space-y-5">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div>
            <label className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider block mb-2">
              Choose a profile and version:
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {loadingAgents ? <p className="text-xs text-slate-500">Loading registered profiles…</p> : candidateAgents.length === 0 ? <p className="text-xs text-slate-500">No active profiles are available for validation.</p> : candidateAgents.map((ag) => (
                <button
                  type="button"
                  key={ag.id}
                  disabled={running || releasePending || exporting}
                  aria-pressed={selectedAgentId === ag.id}
                  onClick={() => {
                    setCurrentReport(null);setReleaseNote('');setError(null);setReleaseBlocked(false);
                    setSelectedAgentId(ag.id);
                    setAgentName(ag.name);
                    setVersion(ag.version);
                  }}
                  className={`p-3 text-left rounded-xl border transition-all text-xs ${
                    selectedAgentId === ag.id
                      ? 'bg-brand-50 border-brand-300 dark:bg-brand-950/50 dark:border-brand-500/50 shadow-sm'
                      : 'bg-white hover:bg-slate-50 dark:bg-slate-900/80 border-slate-200 dark:border-slate-800 dark:hover:border-slate-700 text-slate-600 dark:text-slate-400 shadow-sm'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-slate-900 dark:text-white text-sm">{ag.name}</span>
                    <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700">
                      {ag.version}
                    </span>
                  </div>
                  <div className="text-[11px] text-brand-700 dark:text-brand-400 font-semibold mt-1">{ag.status === 'quarantined' ? 'Quarantined' : ag.status === 'at_risk' ? 'At-Risk Candidate' : ag.status === 'protected' ? 'Protected Profile' : 'Monitoring Profile'}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="w-full md:w-56 shrink-0">
            <label htmlFor="validation-version" className="text-xs font-semibold text-slate-600 dark:text-slate-400 block mb-1">Candidate version</label>
            <input id="validation-version" value={version} onChange={(event) => {setVersion(event.target.value);setCurrentReport(null);setReleaseNote('');setError(null);setReleaseBlocked(false);}} disabled={readOnly || running || releasePending || exporting} className="w-full mb-2 rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-xs text-slate-900 dark:text-white disabled:cursor-not-allowed disabled:opacity-60" placeholder="v2.1.0-rc1" />
            {!readOnly && <button
              type="button"
              onClick={() => handleRunValidation()}
              disabled={running || releasePending || exporting || loadingAgents || !candidateAgents.some(agent=>agent.id===selectedAgentId)}
              className="px-6 py-3 rounded-xl text-xs font-bold bg-brand-600 hover:bg-brand-500 text-white shadow-md shadow-brand-500/20 transition-all flex items-center space-x-2 w-full sm:w-auto justify-center"
            >
              {running ? <Sparkles className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4 fill-white" />}
              <span>{running ? 'Running checks…' : 'Run safety checks'}</span>
            </button>}
          </div>
        </div>
      </div>

      <button type="button" onClick={loadHistory} disabled={releasePending} className="rounded-lg border border-slate-300 px-3 py-2 text-xs font-semibold dark:border-slate-700">Refresh validation history</button>

      {history.length > 0 && (
        <div className="glass-panel p-5 sm:p-6 rounded-2xl transition-all">
          <div className="flex items-center justify-between gap-3 mb-1">
            <div className="flex items-center gap-2">
              <History className="w-4 h-4 text-brand-600 dark:text-brand-400" />
              <h3 className="text-base font-bold text-slate-900 dark:text-white">Recent validation runs</h3>
              <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-bold border border-slate-200 dark:border-slate-700">
                {history.length} Runs
              </span>
            </div>

            <button
              type="button"
              onClick={() => setIsHistoryCollapsed(!isHistoryCollapsed)}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-xl text-xs font-bold text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition"
              aria-expanded={!isHistoryCollapsed}
            >
              <span>{isHistoryCollapsed ? 'Show History' : 'Collapse'}</span>
              {isHistoryCollapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
            </button>
          </div>

          <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">Historical gate evaluations and release promotion records.</p>

          {!isHistoryCollapsed && (
            <div className="space-y-3 animate-fade-in">
              <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800">
                <table className="w-full min-w-[620px] text-left text-xs">
                  <thead className="bg-slate-50 dark:bg-slate-900 text-slate-500 dark:text-slate-400 uppercase font-bold text-[10px] border-b border-slate-200 dark:border-slate-800">
                    <tr>
                      <th className="py-2.5 px-3">Agent</th>
                      <th className="py-2.5 px-3">Version</th>
                      <th className="py-2.5 px-3">Assessment</th>
                      <th className="py-2.5 px-3">Release</th>
                      <th className="py-2.5 px-3">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800/60">
                    {history.slice(0, 10).map((h) => (
                      <tr key={h.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-900/50 transition">
                        <td className="py-2.5 px-3 font-semibold text-slate-900 dark:text-white">{h.agent_name || h.agent_id}</td>
                        <td className="py-2.5 px-3 font-mono">{h.version}</td>
                        <td className="py-2.5 px-3">
                          <span className={`px-2.5 py-0.5 rounded text-[10px] font-bold uppercase ${
                            h.status === 'passed' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-300' : 'bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300'
                          }`}>
                            {h.status === 'passed' ? 'Passed' : 'Review Required'}
                          </span>
                        </td>
                        <td className="py-2.5 px-3">
                          <span className={`px-2.5 py-0.5 rounded text-[10px] font-bold uppercase ${
                            h.release_decision === 'approved' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-300' : h.release_decision === 'rejected' ? 'bg-rose-100 text-rose-800 dark:bg-rose-500/20 dark:text-rose-300' : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300'
                          }`}>
                            {h.release_decision || 'Pending'}
                          </span>
                        </td>
                        <td className="py-2.5 px-3 text-slate-500 font-mono text-[11px]">{new Date(h.created_at).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Validation Results Report */}
      {currentReport && (
        <div className="space-y-6 animate-fade-in">
          
          {/* Executive Summary Card */}
          <div className={`p-6 sm:p-8 rounded-2xl border transition-all ${
            currentReport.overall_status === 'passed'
              ? 'bg-emerald-50/70 border-emerald-300 dark:bg-emerald-950/20 dark:border-emerald-500/30'
              : 'bg-amber-50/70 border-amber-300 dark:bg-amber-950/20 dark:border-amber-500/30'
          }`}>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              
              <div className="space-y-2">
                <div className="flex items-center space-x-3">
                  {currentReport.overall_status === 'passed' ? (
                    <div className="p-2.5 rounded-xl bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/30">
                      <ShieldCheck className="w-6 h-6" />
                    </div>
                  ) : (
                    <div className="p-2.5 rounded-xl bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400 border border-amber-200 dark:border-amber-500/30">
                      <ShieldAlert className="w-6 h-6" />
                    </div>
                  )}

                  <div>
                    <h2 className="text-xl font-extrabold text-slate-900 dark:text-white">
                      Validation result: {currentReport.agent_name}
                    </h2>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      Version: {currentReport.version}
                    </p>
                  </div>
                </div>

                <div className={`p-3.5 rounded-xl text-xs font-medium ${
                  currentReport.overall_status === 'passed'
                    ? 'bg-emerald-100/60 text-emerald-900 dark:bg-emerald-950/50 dark:text-emerald-200 border border-emerald-200 dark:border-emerald-800/40'
                    : 'bg-amber-100/60 text-amber-900 dark:bg-amber-950/50 dark:text-amber-200 border border-amber-200 dark:border-amber-800/40'
                }`}>
                  <span className="font-bold block mb-1">Release Recommendation:</span>
                  <span>{currentReport.recommendation}</span>
                </div>

                <div className="mt-3 rounded-xl border border-slate-200 bg-white/70 p-3 dark:border-slate-800 dark:bg-slate-950/40">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">Release decision</div>
                    <div className="text-xs font-semibold text-slate-800 dark:text-slate-200">
                        {currentReport.release_decision === 'approved' ? 'Human approval recorded for this run.' : currentReport.release_decision === 'rejected' ? 'Rejected — this run does not authorize promotion.' : 'Pending human approval.'}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button type="button" onClick={handleExportReport} disabled={exporting} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-[11px] font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"><Download className="h-3.5 w-3.5" />{exporting ? 'Preparing…' : 'Download report'}</button>
                      {!readOnly && currentReport.release_decision === 'pending' && (
                      <div className="flex flex-wrap gap-2">
                        {currentReport.overall_status === 'passed' && <button type="button" onClick={() => handleReleaseDecision('approve')} disabled={releasePending || releaseBlocked} className="rounded-lg bg-emerald-600 px-3 py-2 text-[11px] font-bold text-white hover:bg-emerald-500 disabled:opacity-50">{releasePending ? 'Recording…' : 'Approve release'}</button>}
                        <button type="button" onClick={() => handleReleaseDecision('reject')} disabled={releasePending || releaseBlocked} className="rounded-lg border border-rose-200 px-3 py-2 text-[11px] font-bold text-rose-700 hover:bg-rose-50 disabled:opacity-50 dark:border-rose-900/50 dark:text-rose-300 dark:hover:bg-rose-950/30">Reject release</button>
                      </div>
                      )}
                    </div>
                  </div>
                  <input aria-label="Release decision note" value={releaseNote} onChange={(event) => setReleaseNote(event.target.value)} disabled={readOnly || releasePending} placeholder="Decision note (optional)" className="mt-2 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-[11px] text-slate-900 dark:border-slate-800 dark:bg-slate-900 dark:text-white disabled:cursor-not-allowed disabled:opacity-60" />
                  <p className="mt-2 text-[10px] text-slate-500 dark:text-slate-400">Your decision applies to the profile and policy settings tested in this run. Older unbound or changed evidence needs new checks before approval.</p>
                </div>
              </div>

              <div className="flex items-center space-x-6 self-start md:self-center">
                <div className="text-center">
                  <div className="text-xs text-slate-500 dark:text-slate-400 font-semibold">Run governance status</div>
                  <span className={`inline-block mt-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold uppercase ${
                    currentReport.overall_status === 'passed'
                      ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-500/30'
                      : 'bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300 border border-amber-300 dark:border-amber-500/30'
                  }`}>
                    {releaseBlocked ? 'New checks required' : currentReport.release_decision === 'rejected' ? 'Release rejected' : currentReport.overall_status !== 'passed' ? 'Checks need review' : currentReport.release_decision === 'approved' ? 'Approval recorded' : 'Awaiting human approval'}
                  </span>
                </div>
              </div>

            </div>
          </div>

          {/* Test Suites Breakdown */}
          <div className="glass-panel p-6 rounded-2xl space-y-4">
            <h3 className="text-base font-bold text-slate-900 dark:text-white">Safety check results</h3>
            <p className="text-xs text-slate-500 dark:text-slate-400">Open a check to see the individual scenarios.</p>

            <div className="space-y-3">
              {currentReport.suites.map((suite, idx) => {
                const isExpanded = expandedSuite === idx;
                const allPassed = suite.passed_count === suite.total_count;

                return (
                  <div key={idx} className="rounded-xl bg-slate-50 dark:bg-slate-900/80 border border-slate-200 dark:border-slate-800 overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setExpandedSuite(isExpanded ? -1 : idx)}
                      className="w-full p-4 flex items-center justify-between text-left hover:bg-slate-100 dark:hover:bg-slate-800/40 transition-all text-xs"
                    >
                      <div className="flex items-center space-x-3">
                        {allPassed ? (
                          <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                        ) : (
                          <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                        )}
                        <div>
                          <span className="font-bold text-slate-900 dark:text-white text-sm">{suite.name}</span>
                          <p className="text-[11px] text-slate-500 dark:text-slate-400">{suite.description}</p>
                        </div>
                      </div>

                      <div className="flex items-center space-x-3">
                        <span className={`font-mono font-semibold text-xs ${allPassed ? 'text-emerald-700 dark:text-emerald-400' : 'text-amber-700 dark:text-amber-400'}`}>
                          {suite.passed_count}/{suite.total_count} Passed
                        </span>
                        {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                      </div>
                    </button>

                    {isExpanded && (
                      <div className="p-4 pt-0 border-t border-slate-200 dark:border-slate-800/60 space-y-2.5 bg-white/50 dark:bg-slate-950/40">
                        {suite.tests.map((test) => (
                          <div 
                            key={test.id}
                            className={`p-3 rounded-lg border text-xs space-y-1.5 ${
                              test.status === 'passed'
                                ? 'bg-white dark:bg-slate-900/60 border-slate-200 dark:border-slate-800'
                                : 'bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-500/30'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <span className="font-bold text-slate-900 dark:text-slate-200">{test.name}</span>
                              <span className={`text-[10px] px-2 py-0.5 rounded font-semibold ${
                                test.status === 'passed'
                                  ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300'
                                  : 'bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300'
                              }`}>
                                Expected: {test.expected_decision} | Actual: {test.actual_decision}
                              </span>
                            </div>
                            <p className="text-[11px] text-slate-600 dark:text-slate-400">{test.description}</p>
                            <p className={`text-[11px] font-mono ${
                              test.status === 'passed' ? 'text-emerald-700 dark:text-emerald-400' : 'text-amber-700 dark:text-amber-300'
                            }`}>
                              Observation: {test.observation}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="mt-6 pt-4 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between">
              <span className="text-xs text-slate-500 dark:text-slate-400">Deterministic checks are evidence for this run, not a safety certification. Changed profiles or policy settings need new checks.</span>
              <button
                type="button"
                onClick={() => setActiveTab('simulate')}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-brand-600 hover:bg-brand-500 text-white font-bold text-xs shadow-sm transition"
              >
                <span>Stage 4: Test In-Flight Actions in Simulator</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

        </div>
      )}

    </div>
  );
};
